import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import {
    Box, DialogContent, DialogTitle,
    FormControlLabel, MenuItem, Switch, TextField, Typography,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';
import OwnerSelectionCard from 'app/shared-components/OwnerSelectionCard';
import { ACCELERATOR_LABELS } from '../../util/enums';

import * as formHelpers from './formHelpers';
import { isN1Instance } from './formHelpers';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import BasicInformationSection from '../components/BasicInformationSection.temporary';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration.temporary';
import StorageSettingsSection from '../components/StorageSettingsSection.temporary';
import CredentialSelector from '../components/CredentialSelector.temporary';
import RegionSelector from '../components/RegionSelector.temporary';
import ZoneSelector from '../components/ZoneSelector.temporary';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import CapacityReservationsSection from '../components/CapacityReservationsSection.temporary';
import DescriptionSection from '../components/DescriptionSection.temporary';
import ComputeNodesSection from '../components/ComputeNodesSection';
import ComputeNodeGroupFields from '../components/ComputeNodeGroupFields';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';
import TpuSection from './TpuSection';

const GcpCluster = (props) => {
    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace, selectedOwner, setSelectedOwner,
        disableInputs, isFormValid, volSizeLimits,
        availability, quotas, handleClose, handleSubmit, handleEdit,
    } = useClusterForm({
        provider: 'gcp', props, formHelpers,
        extraValidation: (f) => Boolean(f.zoneId),
    });

    const {
        isLoading: isAvailabilityLoading, error: availabilityError,
        unableToVerify, selectedZoneAvailable, selectedZoneMissing,
    } = availability;

    // GCP cost estimate
    const providers = useSelector(({ providers }) => providers.providers.list);
    const costEstimate = useMemo(() => {
        if (!form?.regionId || !providers?.length) return null;
        const gcp = providers.find(p => p.id === 'gcp');
        if (!gcp?.instanceSizes) return null;
        let total = 0;
        const getCost = (id) => gcp.instanceSizes.find(s => s.instance_id === id)?.cost?.cost_region?.[form.regionId] || 0;
        total += getCost(form.serverInstanceId);
        (form.nodeGroups || []).forEach(g => { total += getCost(g.instance_id) * parseInt(g.count || 0, 10); });
        return total > 0 ? total.toFixed(2) : null;
    }, [form?.regionId, form?.serverInstanceId, form?.nodeGroups, providers]);

    // GCP: clear accelerators for non-n1 instances
    const handleServerInstanceChange = useCallback((instanceId) => {
        const changes = { serverInstanceId: instanceId };
        if (!isN1Instance(instanceId)) { changes.serverAcceleratorType = null; changes.serverAcceleratorCount = 0; }
        setField(changes);
    }, [setField]);

    const handleComputeInstanceChange = useCallback((index, instanceId) => {
        const updated = [...(form?.nodeGroups || [])];
        const g = { ...updated[index], instance_id: instanceId };
        if (!isN1Instance(instanceId)) { g.accelerator_type = null; g.accelerator_count = 0; }
        updated[index] = g;
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, setField]);

    const renderGcpGroupFields = useCallback((group, index, { updateGroup }) => (
        <ComputeNodeGroupFields
            group={group} index={index} updateGroup={updateGroup}
            provider="gcp" imageMode={form?.imageMode} maxCount={250}
            onInstanceChange={handleComputeInstanceChange}
            allowedInstanceIds={marketplace.allowedComputeIds}
            disableStackFields={marketplace.disableStackFields}
            disabled={disableInputs}
        >
            {isN1Instance(group.instance_id) && (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'flex-start' }}>
                    <TextField
                        select label="Accelerator Type" variant="outlined"
                        className={clsx(classes.input)} style={{ flex: 1 }}
                        value={group.accelerator_type || ''}
                        onChange={(e) => {
                            updateGroup(index, 'accelerator_type', e.target.value || null);
                            if (!e.target.value) updateGroup(index, 'accelerator_count', 0);
                            else if (!group.accelerator_count) updateGroup(index, 'accelerator_count', 1);
                        }}
                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                    >
                        {Object.entries(ACCELERATOR_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </TextField>
                    <TextField
                        label="GPU Count" type="number" variant="outlined"
                        className={clsx(classes.input)} style={{ width: '120px' }}
                        value={group.accelerator_count || 0}
                        onChange={(e) => updateGroup(index, 'accelerator_count', parseInt(e.target.value, 10) || 0)}
                        disabled={!group.accelerator_type} inputProps={{ min: 0, max: 8 }}
                    />
                </div>
            )}
        </ComputeNodeGroupFields>
    ), [form?.imageMode, marketplace, handleComputeInstanceChange, disableInputs, classes.input]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="gcp" providerLabel="Google Cloud" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={handleSubmit} onEdit={handleEdit} onClose={handleClose}
                            subtitle={costEstimate ? `— Est. $${costEstimate}/hr` : undefined}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">
                                <OwnerSelectionCard
                                    value={selectedOwner} onChange={setSelectedOwner}
                                    label="Cluster Owner" title="Cluster Owner"
                                    showInAdvancedMode={true} advanced={advanced} disabled={disableInputs}
                                />
                                <BasicInformationSection
                                    form={form} setField={setField} provider="gcp"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                />

                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard icon="cloud" title="Infrastructure" fillHeight
                                            action={form.zoneId ? (
                                                <ZoneAvailabilityIndicator
                                                    isLoading={isAvailabilityLoading} isAvailable={selectedZoneAvailable}
                                                    missingInstances={selectedZoneMissing} error={availabilityError}
                                                    unableToVerify={unableToVerify}
                                                />
                                            ) : null}
                                        >
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="gcp" value={form.credentialId}
                                                    onChange={(id) => setField('credentialId', id)} />
                                                <InstanceSizeSelector provider="gcp" value={form.serverInstanceId || ''}
                                                    onChange={handleServerInstanceChange}
                                                    allowedInstanceIds={marketplace.config?.instance_type} label="Head Node Size" />
                                            </div>
                                            {isN1Instance(form.serverInstanceId) && (
                                                <div style={inputGroupStyle}>
                                                    <TextField
                                                        select label="Head Node Accelerator" variant="outlined"
                                                        className={clsx(classes.input)} style={{ flex: 1 }}
                                                        value={form.serverAcceleratorType || ''}
                                                        onChange={(e) => {
                                                            const type = e.target.value || null;
                                                            setField({ serverAcceleratorType: type, serverAcceleratorCount: type ? (form.serverAcceleratorCount || 1) : 0 });
                                                        }}
                                                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                                                    >
                                                        {Object.entries(ACCELERATOR_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                                                    </TextField>
                                                    <TextField label="Count" type="number" variant="outlined"
                                                        className={clsx(classes.input)} style={{ width: '120px' }}
                                                        value={form.serverAcceleratorCount || 0}
                                                        onChange={(e) => setField('serverAcceleratorCount', parseInt(e.target.value, 10) || 0)}
                                                        disabled={!form.serverAcceleratorType} inputProps={{ min: 0, max: 8 }} />
                                                </div>
                                            )}
                                            <div style={inputGroupStyle}>
                                                <RegionSelector provider="gcp" value={form.regionId || ''} onChange={(id) => setField('regionId', id)} />
                                                <ZoneSelector provider="gcp" regionId={form.regionId || ''} value={form.zoneId || ''} onChange={(id) => setField('zoneId', id)} />
                                            </div>
                                            {advanced && (
                                                <Box mt={2} display="flex" alignItems="center">
                                                    <FormControlLabel
                                                        control={<Switch checked={form.spotVm || false} onChange={(e) => setField('spotVm', e.target.checked)} color="primary" />}
                                                        label={<Typography variant="body2">Enable Spot/Preemptible Instances</Typography>}
                                                    />
                                                </Box>
                                            )}
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        <ResourceManagerConfiguration form={form} setField={setField} hideBursting={marketplace.isMarketStack} lockManager={Boolean(marketplace.config?.resource_manager)} />
                                    </div>
                                </div>

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="gcp" advanced={advanced} marketplace={marketplace} disableInputs={disableInputs}
                                />
                                {advanced && <CapacityReservationsSection form={form} setField={setField} fillHeight showZoneFallback />}
                                {advanced && <StorageSettingsSection form={form} setField={setField} provider="gcp" volSizeLimits={volSizeLimits} />}

                                <ComputeNodesSection form={form} setField={setField} disabled={disableInputs} maxGroups={250} renderGroupFields={renderGcpGroupFields} />
                                {advanced && <TpuSection form={form} setField={setField} />}
                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default GcpCluster;
